// New-campaign page mounted at /campaigns/new.
//
// Responsibilities:
//   1. Collect a CampaignConfig from the user.
//   2. Optionally populate it from a preset or from a registered oracle
//      dataset (feature-flag gated).
//   3. Validate inline.
//   4. POST to the sampler service via ../api createCampaign and route to
//      the new campaign's detail page.

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Callout,
  Checkbox,
  DropdownMenu,
  Flex,
  Heading,
  Select,
  Switch,
  Text,
  TextField,
} from '@radix-ui/themes';

import {
  AxisSpec,
  CampaignConfig,
  CANONICAL_INPUT_DEFAULTS,
  CANONICAL_OUTPUTS,
  CanonicalInputName,
  CanonicalOutputName,
  OracleDataset,
  SolverChoice,
} from '../types';
import { createCampaign, useOraclesQuery } from '../api';
import { isOracleModeEnabled } from '../featureFlags';
import { ThreadLogo } from '../../../components/ThreadLogo';

import AxisChip from './AxisChip';
import OutputRow from './OutputRow';
import { PRESETS } from './presets';
import { validateCampaignConfig } from './validation';

const LOW_FI_SOLVERS: SolverChoice[] = ['AVL', 'JVL', 'DATCOM', 'panel'];
const HIGH_FI_SOLVERS: SolverChoice[] = [
  'AVL',
  'JVL',
  'DATCOM',
  'panel',
  'Flow360',
];

const ALL_CANONICAL_INPUTS = Object.keys(
  CANONICAL_INPUT_DEFAULTS,
) as CanonicalInputName[];

function shortRandomId(): string {
  return Math.random().toString(36).slice(2, 7);
}

function makeAxis(name: CanonicalInputName): AxisSpec {
  const defaults = CANONICAL_INPUT_DEFAULTS[name];
  const [min, max] = defaults.defaultRange ?? [0, 1];
  return { name, units: defaults.units, mode: 'range', min, max };
}

export interface CampaignConfiguratorProps {
  /** 'page' = full-screen route (default); 'panel' = embedded in SlidePanel. */
  mode?: 'page' | 'panel';
  /** Called with the new campaign id on successful create. If provided, the
   *  component does not navigate; the caller controls transition. */
  onCreated?: (campaignId: string) => void;
}

export default function CampaignConfigurator(props: CampaignConfiguratorProps = {}) {
  const { mode = 'page', onCreated } = props;
  const navigate = useNavigate();
  const oracleModeEnabled = isOracleModeEnabled();

  // Initial config: use the Empty preset so we start with alpha + beta + all
  // outputs pre-selected. User can swap via the Presets menu.
  const [config, setConfig] = useState<CampaignConfig>(() => {
    const base = PRESETS.empty.build();
    return { ...base, name: `${base.name} ${shortRandomId()}` };
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Oracle datasets, loaded only when the feature flag is on.
  const oraclesQuery = useOraclesQuery();
  const oracles: OracleDataset[] = oraclesQuery.data ?? [];

  const activeOracle: OracleDataset | undefined = useMemo(() => {
    if (!config.oracle.enabled || !config.oracle.datasetId) return undefined;
    return oracles.find((o) => o.id === config.oracle.datasetId);
  }, [config.oracle, oracles]);

  const oracleLocksInputs = Boolean(
    config.oracle.enabled && activeOracle !== undefined,
  );

  const outputAvailability: Record<CanonicalOutputName, boolean> = useMemo(() => {
    const m: Record<CanonicalOutputName, boolean> = {
      CL: true, CD: true, CY: true, Cmx: true, Cmy: true, Cmz: true,
    };
    if (!activeOracle) return m;
    for (const mapping of activeOracle.outputMappings) {
      if (mapping.canonicalName in m) {
        m[mapping.canonicalName as CanonicalOutputName] = mapping.available;
      }
    }
    return m;
  }, [activeOracle]);

  const validation = useMemo(
    () => validateCampaignConfig(config, activeOracle),
    [config, activeOracle],
  );
  const errors = validation.ok ? {} : validation.errors;

  // ---- form mutators -----------------------------------------------------

  const applyPreset = (key: keyof typeof PRESETS) => {
    const next = PRESETS[key].build();
    setConfig({ ...next, name: `${next.name} ${shortRandomId()}` });
    setSubmitError(null);
  };

  const updateConfig = <K extends keyof CampaignConfig>(
    key: K,
    value: CampaignConfig[K],
  ) => {
    setConfig((c) => ({ ...c, [key]: value }));
  };

  const updateAxis = (idx: number, next: AxisSpec) => {
    setConfig((c) => {
      const inputs = c.inputs.slice();
      inputs[idx] = next;
      return { ...c, inputs };
    });
  };

  const removeAxis = (idx: number) => {
    setConfig((c) => ({
      ...c,
      inputs: c.inputs.filter((_, i) => i !== idx),
    }));
  };

  const addAxis = (name: CanonicalInputName) => {
    if (config.inputs.some((a) => a.name === name)) return;
    setConfig((c) => ({ ...c, inputs: [...c.inputs, makeAxis(name)] }));
  };

  const toggleOutput = (name: CanonicalOutputName, next: boolean) => {
    setConfig((c) => {
      const exists = c.outputs.find((o) => o.name === name);
      if (next && !exists) {
        return { ...c, outputs: [...c.outputs, { name, weight: 1 }] };
      }
      if (!next && exists) {
        return { ...c, outputs: c.outputs.filter((o) => o.name !== name) };
      }
      return c;
    });
  };

  const updateOutputWeight = (name: CanonicalOutputName, weight: number) => {
    setConfig((c) => ({
      ...c,
      outputs: c.outputs.map((o) => (o.name === name ? { ...o, weight } : o)),
    }));
  };

  const updateOutputTargetSigma = (
    name: CanonicalOutputName,
    targetSigma: number | undefined,
  ) => {
    setConfig((c) => ({
      ...c,
      outputs: c.outputs.map((o) =>
        o.name === name ? { ...o, targetSigma } : o,
      ),
    }));
  };

  const onOracleToggle = (enabled: boolean) => {
    setConfig((c) => ({
      ...c,
      oracle: enabled
        ? { enabled: true, datasetId: c.oracle.datasetId }
        : { enabled: false },
    }));
  };

  const onOracleSelect = (datasetId: string) => {
    const dataset = oracles.find((o) => o.id === datasetId);
    if (!dataset) {
      setConfig((c) => ({ ...c, oracle: { enabled: true, datasetId } }));
      return;
    }
    // Populate inputs from dataset axes
    const oracleInputs: AxisSpec[] = dataset.axes.map((a) => ({
      name: a.name,
      units: a.units,
      mode: 'range',
      min: a.min,
      max: a.max,
    }));
    // Filter outputs: keep only those available in this oracle, preserve
    // existing weights/targetSigma where possible.
    const availableOutputNames = dataset.outputMappings
      .filter((m) => m.available)
      .map((m) => m.canonicalName);
    setConfig((c) => {
      const prevByName = new Map(c.outputs.map((o) => [o.name, o]));
      const nextOutputs = availableOutputNames.map(
        (name) => prevByName.get(name) ?? { name, weight: 1 },
      );
      return {
        ...c,
        inputs: oracleInputs,
        outputs: nextOutputs,
        oracle: { enabled: true, datasetId },
      };
    });
  };

  // ---- submission --------------------------------------------------------

  const canSubmit = validation.ok && !submitting;

  const handleSubmit = async () => {
    if (!validation.ok) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { id } = await createCampaign(config);
      if (onCreated) {
        onCreated(id);
      } else {
        navigate(`/campaigns/${id}`);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create campaign.';
      setSubmitError(message);
      setSubmitting(false);
    }
  };

  // ---- derived display ---------------------------------------------------

  const selectedOutputNames = new Set(config.outputs.map((o) => o.name));
  const unusedInputs = ALL_CANONICAL_INPUTS.filter(
    (n) => !config.inputs.some((a) => a.name === n),
  );

  const estimatedMaxCost = config.budget * config.flexCreditsPerHighFiRun;

  const isPanel = mode === 'panel';

  return (
    <Box
      style={{
        minHeight: isPanel ? '100%' : '100vh',
        background: isPanel ? 'transparent' : 'var(--color-background)',
        color: 'var(--gray-12)',
        padding: isPanel ? '16px 20px' : '24px 32px',
      }}
    >
      <Flex direction="column" gap="5" style={{ maxWidth: isPanel ? '100%' : 980, margin: '0 auto' }}>
        {/* Header — only in standalone mode; panel mode gets chrome from SlidePanel */}
        {!isPanel && (
          <Flex justify="between" align="center">
            <Flex align="center" gap="3">
              <Link to="/" aria-label="Back to Thread">
                <ThreadLogo height={20} />
              </Link>
              <Text size="2" color="gray">/</Text>
              <Heading size="5">New adaptive sampling campaign</Heading>
            </Flex>
            <Link to="/" style={{ color: 'var(--accent-11)' }}>
              Back to projects
            </Link>
          </Flex>
        )}
        {isPanel && (
          <Heading size="4">Configure campaign</Heading>
        )}

        {/* Name + preset */}
        <Flex gap="3" align="end" wrap="wrap">
          <Box style={{ flex: 1, minWidth: 260 }}>
            <Text as="label" size="2" weight="medium">
              Name
            </Text>
            <TextField.Root
              value={config.name}
              onChange={(e) => updateConfig('name', e.currentTarget.value)}
              placeholder="Campaign name"
            />
            {errors.name && (
              <Text size="1" color="red">
                {errors.name}
              </Text>
            )}
          </Box>

          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Button variant="soft">Use a preset</Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {Object.entries(PRESETS).map(([key, p]) => (
                <DropdownMenu.Item
                  key={key}
                  onSelect={() => applyPreset(key as keyof typeof PRESETS)}
                >
                  {p.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </Flex>

        {/* Oracle mode */}
        {oracleModeEnabled && (
          <Box
            style={{
              border: '1px solid var(--gray-a5)',
              borderRadius: 8,
              padding: '14px 16px',
            }}
          >
            <Flex align="center" gap="3" justify="between">
              <Flex gap="2" align="center">
                <Switch
                  checked={config.oracle.enabled}
                  onCheckedChange={onOracleToggle}
                />
                <Text weight="medium">
                  Use an oracle dataset (Phase 1 validation)
                </Text>
              </Flex>
              {oraclesQuery.isLoading && config.oracle.enabled && (
                <Text size="1" color="gray">
                  Loading oracles...
                </Text>
              )}
            </Flex>

            {config.oracle.enabled && (
              <Flex direction="column" gap="3" style={{ marginTop: 12 }}>
                <Select.Root
                  value={config.oracle.datasetId ?? ''}
                  onValueChange={onOracleSelect}
                >
                  <Select.Trigger placeholder="Choose an oracle dataset" />
                  <Select.Content>
                    {oracles.map((o) => (
                      <Select.Item key={o.id} value={o.id}>
                        {o.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>

                {activeOracle && (
                  <Callout.Root color="blue" variant="soft">
                    <Callout.Text>
                      Inputs are locked to this oracle&apos;s axes. Outputs not
                      present in the oracle are greyed out.
                    </Callout.Text>
                  </Callout.Root>
                )}
              </Flex>
            )}
          </Box>
        )}

        {/* Inputs */}
        <Box>
          <Flex align="center" justify="between" style={{ marginBottom: 8 }}>
            <Heading size="3">Inputs</Heading>
            {!oracleLocksInputs && unusedInputs.length > 0 && (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <Button size="1" variant="soft">
                    + add
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  {unusedInputs.map((n) => (
                    <DropdownMenu.Item key={n} onSelect={() => addAxis(n)}>
                      {n}{' '}
                      <Text size="1" color="gray">
                        ({CANONICAL_INPUT_DEFAULTS[n].units})
                      </Text>
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            )}
          </Flex>
          <Flex direction="column" gap="2">
            {config.inputs.map((axis, idx) => (
              <AxisChip
                key={`${axis.name}-${idx}`}
                axis={axis}
                onChange={(next) => updateAxis(idx, next)}
                onRemove={
                  oracleLocksInputs ? undefined : () => removeAxis(idx)
                }
                locked={oracleLocksInputs}
                error={errors[`inputs.${idx}`]}
              />
            ))}
          </Flex>
          {errors.inputs && (
            <Text size="1" color="red">
              {errors.inputs}
            </Text>
          )}
        </Box>

        {/* Outputs */}
        <Box>
          <Heading size="3" style={{ marginBottom: 8 }}>
            Outputs
          </Heading>
          <Flex direction="column" gap="1">
            {CANONICAL_OUTPUTS.map((name) => {
              const available = outputAvailability[name];
              const spec = config.outputs.find((o) => o.name === name);
              return (
                <OutputRow
                  key={name}
                  name={name}
                  selected={selectedOutputNames.has(name)}
                  spec={spec}
                  disabled={!available}
                  disabledReason={
                    !available
                      ? 'Not present in this oracle dataset'
                      : undefined
                  }
                  onToggle={(next) => toggleOutput(name, next)}
                  onWeightChange={(w) => updateOutputWeight(name, w)}
                  onTargetSigmaChange={(v) =>
                    updateOutputTargetSigma(name, v)
                  }
                />
              );
            })}
          </Flex>
          {errors.outputs && (
            <Text size="1" color="red">
              {errors.outputs}
            </Text>
          )}
        </Box>

        {/* Budget / batch / solvers */}
        <Box>
          <Heading size="3" style={{ marginBottom: 8 }}>
            Budget and solvers
          </Heading>
          <Flex gap="4" wrap="wrap">
            <Box style={{ minWidth: 140 }}>
              <Text as="label" size="2">
                Budget
              </Text>
              <TextField.Root
                type="number"
                value={config.budget}
                onChange={(e) =>
                  updateConfig('budget', Number(e.currentTarget.value))
                }
              />
              {errors.budget && (
                <Text size="1" color="red">
                  {errors.budget}
                </Text>
              )}
            </Box>
            <Box style={{ minWidth: 140 }}>
              <Text as="label" size="2">
                Batch size
              </Text>
              <TextField.Root
                type="number"
                value={config.batchSize}
                onChange={(e) =>
                  updateConfig('batchSize', Number(e.currentTarget.value))
                }
              />
              {errors.batchSize && (
                <Text size="1" color="red">
                  {errors.batchSize}
                </Text>
              )}
            </Box>
            <Box style={{ minWidth: 160 }}>
              <Text as="label" size="2">
                Low-fi solver
              </Text>
              <Select.Root
                value={config.lowFiSolver}
                onValueChange={(v) =>
                  updateConfig('lowFiSolver', v as SolverChoice)
                }
              >
                <Select.Trigger />
                <Select.Content>
                  {LOW_FI_SOLVERS.map((s) => (
                    <Select.Item key={s} value={s}>
                      {s}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>
            <Box style={{ minWidth: 160 }}>
              <Text as="label" size="2">
                High-fi solver
              </Text>
              <Select.Root
                value={config.highFiSolver}
                onValueChange={(v) =>
                  updateConfig('highFiSolver', v as SolverChoice)
                }
              >
                <Select.Trigger />
                <Select.Content>
                  {HIGH_FI_SOLVERS.map((s) => (
                    <Select.Item key={s} value={s}>
                      {s}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>
            <Box style={{ minWidth: 160 }}>
              <Text as="label" size="2">
                Compute pool
              </Text>
              <TextField.Root
                value={config.computePool}
                onChange={(e) =>
                  updateConfig('computePool', e.currentTarget.value)
                }
              />
            </Box>
            <Box style={{ minWidth: 180 }}>
              <Text as="label" size="2">
                FlexCredits per high-fi run
              </Text>
              <TextField.Root
                type="number"
                value={config.flexCreditsPerHighFiRun}
                onChange={(e) =>
                  updateConfig(
                    'flexCreditsPerHighFiRun',
                    Number(e.currentTarget.value),
                  )
                }
              />
            </Box>
          </Flex>

          <Flex gap="2" align="center" style={{ marginTop: 12 }}>
            <Checkbox
              checked={config.stopEarlyIfTargetsMet}
              onCheckedChange={(v) =>
                updateConfig('stopEarlyIfTargetsMet', Boolean(v))
              }
            />
            <Text size="2">Stop early if targets met</Text>
          </Flex>
        </Box>

        {/* Cost preview */}
        <Box
          style={{
            padding: '12px 14px',
            borderRadius: 8,
            background: 'var(--accent-a3)',
          }}
        >
          <Flex justify="between" align="center">
            <Text weight="medium">Estimated max cost</Text>
            <Badge color="iris" size="2">
              {estimatedMaxCost} FC
            </Badge>
          </Flex>
          <Text size="1" color="gray">
            {config.budget} runs x {config.flexCreditsPerHighFiRun} FC per
            high-fi run
          </Text>
        </Box>

        {submitError && (
          <Callout.Root color="red">
            <Callout.Text>{submitError}</Callout.Text>
          </Callout.Root>
        )}

        {/* Submit */}
        <Flex justify="end" gap="2">
          <Button variant="soft" color="gray" onClick={() => navigate('/')}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={handleSubmit}
            loading={submitting}
          >
            Create campaign
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}
